import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, CheckIcon, StarIcon, TrendingUpIcon, UserIcon, CalendarIcon, CreditCardIcon, BuildingIcon, WalletIcon, CheckCircleIcon, Loader2Icon, FileTextIcon, ArrowRightIcon, CarIcon, BriefcaseIcon, HeartPulseIcon, HomeIcon, StethoscopeIcon, SmartphoneIcon, PlaneIcon, GraduationCapIcon, TargetIcon, InfoIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
import { formatNaira, formatDate } from '../utils/format';
interface PlanSelectionProps {
  navigate: (screen: string, productId?: string) => void;
  productId?: string;
  viewMode?: string;
}
type Step = 'plans' | 'onboarding' | 'payment' | 'review' | 'processing' | 'success';
interface Plan {
  id: string;
  name: string;
  type: string;
  premium: number;
  returns: string;
  features: string[];
  popular: boolean;
}
interface OnboardingField {
  id: string;
  label: string;
  type: 'text' | 'date' | 'select';
  options?: string[];
  placeholder?: string;
  icon?: React.ElementType;
}
interface ProductConfig {
  id: string;
  name: string;
  onboardingTitle: string;
  onboardingDesc: string;
  onboardingFields: OnboardingField[];
  plans: Plan[];
  underwriter: string;
  certificateTitle: string;
  icon: React.ElementType;
  color: string;
  pricingType?: 'flexible' | 'fixed';
}
const productConfigs: Record<string, ProductConfig> = {
  goalguard: {
    id: 'goalguard',
    name: 'GoalGuard',
    pricingType: 'flexible',
    onboardingTitle: 'Set Your Goals',
    onboardingDesc: 'Tell us what you are saving for so we can tailor your plan.',
    icon: ShieldCheckIcon,
    color: 'emerald',
    underwriter: 'Leadway Assurance Plc',
    certificateTitle: 'GoalGuard Savings Plan',
    onboardingFields: [{
      id: 'goalName',
      label: 'Goal Name',
      type: 'text',
      placeholder: 'e.g. New Car Fund',
      icon: TargetIcon
    }, {
      id: 'targetAmount',
      label: 'Target Amount',
      type: 'text',
      placeholder: 'e.g. ₦5,000,000',
      icon: WalletIcon
    }, {
      id: 'timeline',
      label: 'Timeline',
      type: 'select',
      options: ['6 Months', '1 Year', '2 Years', '3 Years', '5 Years']
    }],
    plans: [{
      id: 'lite',
      name: 'GoalGuard Lite',
      type: 'Starter',
      premium: 5000,
      returns: '8% Projected',
      features: ['Basic Life Cover', 'Standard Growth', 'Quarterly Statements'],
      popular: false
    }, {
      id: 'plus',
      name: 'GoalGuard Plus',
      type: 'Growth',
      premium: 15000,
      returns: '10% Projected',
      features: ['Enhanced Life Cover', 'Enhanced Growth', 'Monthly Statements', 'Goal Tracking Dashboard'],
      popular: true
    }, {
      id: 'max',
      name: 'GoalGuard Max',
      type: 'Accelerator',
      premium: 30000,
      returns: '12% Projected',
      features: ['Maximum Life Cover', 'Aggressive Growth', 'Weekly Statements', 'Priority Support', 'Flexible Withdrawals'],
      popular: false
    }]
  },
  'child-education': {
    id: 'child-education',
    name: 'Future Scholar Fund',
    pricingType: 'flexible',
    onboardingTitle: 'Who Is This For?',
    onboardingDesc: 'Enter the details of the child who will benefit from this education fund.',
    icon: GraduationCapIcon,
    color: 'emerald',
    underwriter: 'Leadway Assurance Plc',
    certificateTitle: 'Future Scholar Fund Plan',
    onboardingFields: [{
      id: 'childName',
      label: "Child's Full Name",
      type: 'text',
      placeholder: 'e.g. David Okonkwo',
      icon: UserIcon
    }, {
      id: 'childDob',
      label: 'Date of Birth',
      type: 'date',
      icon: CalendarIcon
    }, {
      id: 'relationship',
      label: 'Your Relationship',
      type: 'select',
      options: ['Parent', 'Legal Guardian', 'Grandparent', 'Other Relative']
    }],
    plans: [{
      id: 'basic',
      name: 'Plan A',
      type: 'Standard',
      premium: 15500,
      returns: '8% Projected',
      features: ['Basic Life Cover', 'Standard Growth', 'Annual Statements'],
      popular: false
    }, {
      id: 'plus',
      name: 'Plan B',
      type: 'Plus',
      premium: 16666,
      returns: '10% Projected',
      features: ['Enhanced Life Cover', 'Enhanced Growth', 'Quarterly Statements', 'Critical Illness Add-on'],
      popular: true
    }, {
      id: 'premium',
      name: 'Plan C',
      type: 'Premium',
      premium: 18200,
      returns: '12% Projected',
      features: ['Maximum Life Cover', 'Aggressive Growth', 'Monthly Statements', 'Contribution Waiver'],
      popular: false
    }]
  },
  'job-loss': {
    id: 'job-loss',
    name: 'Income Shield',
    onboardingTitle: 'Employment Details',
    onboardingDesc: 'Provide your current employment information to set up your income protection.',
    icon: BriefcaseIcon,
    color: 'blue',
    underwriter: 'Anchor Insurance',
    certificateTitle: 'Income Shield Plan',
    onboardingFields: [{
      id: 'employerName',
      label: 'Employer Name',
      type: 'text',
      placeholder: 'e.g. Acme Corp Ltd',
      icon: BuildingIcon
    }, {
      id: 'jobTitle',
      label: 'Job Title',
      type: 'text',
      placeholder: 'e.g. Marketing Manager',
      icon: BriefcaseIcon
    }, {
      id: 'startDate',
      label: 'Employment Start Date',
      type: 'date',
      icon: CalendarIcon
    }],
    plans: [{
      id: 'basic',
      name: 'Basic Cover',
      type: '3 Months',
      premium: 2500,
      returns: 'No Savings',
      features: ['50% Salary Replacement', '3 Months Payout', 'CV Review Assistance'],
      popular: false
    }, {
      id: 'standard',
      name: 'Standard Cover',
      type: '6 Months',
      premium: 4500,
      returns: 'No Savings',
      features: ['60% Salary Replacement', '6 Months Payout', 'Interview Coaching'],
      popular: true
    }, {
      id: 'premium',
      name: 'Premium Cover',
      type: '12 Months',
      premium: 8000,
      returns: 'No Savings',
      features: ['70% Salary Replacement', '12 Months Payout', 'Career Transition Support'],
      popular: false
    }]
  },
  motor: {
    id: 'motor',
    name: 'AutoGuard',
    onboardingTitle: 'Vehicle Details',
    onboardingDesc: 'Enter the details of the vehicle you wish to protect.',
    icon: CarIcon,
    color: 'indigo',
    underwriter: 'AXA Mansard',
    certificateTitle: 'AutoGuard Plan',
    onboardingFields: [{
      id: 'makeModel',
      label: 'Vehicle Make & Model',
      type: 'text',
      placeholder: 'e.g. Toyota Corolla',
      icon: CarIcon
    }, {
      id: 'year',
      label: 'Year of Manufacture',
      type: 'text',
      placeholder: 'e.g. 2019',
      icon: CalendarIcon
    }, {
      id: 'plateNumber',
      label: 'Plate Number',
      type: 'text',
      placeholder: 'e.g. KJA-123XY',
      icon: FileTextIcon
    }],
    plans: [{
      id: 'third-party',
      name: 'Third Party',
      type: 'Basic',
      premium: 5000,
      returns: 'No Savings',
      features: ['Third Party Property Damage', 'Third Party Bodily Injury', 'Legal Liability'],
      popular: false
    }, {
      id: 'comprehensive',
      name: 'Comprehensive',
      type: 'Standard',
      premium: 15000,
      returns: 'Optional Savings',
      features: ['Accidental Damage', 'Theft & Fire', 'Third Party Cover', 'Free Tracker'],
      popular: true
    }, {
      id: 'comprehensive-plus',
      name: 'Comprehensive+',
      type: 'Premium',
      premium: 25000,
      returns: 'Optional Savings',
      features: ['All Comprehensive Benefits', 'Courtesy Car', 'Excess Waiver', 'Roadside Assistance'],
      popular: false
    }]
  },
  life: {
    id: 'life',
    name: 'Legacy Plan',
    pricingType: 'flexible',
    onboardingTitle: 'Beneficiary Details',
    onboardingDesc: 'Who should receive the payout in the event of a claim?',
    icon: HeartPulseIcon,
    color: 'rose',
    underwriter: 'Leadway Assurance Plc',
    certificateTitle: 'Legacy Plan',
    onboardingFields: [{
      id: 'beneficiaryName',
      label: 'Beneficiary Full Name',
      type: 'text',
      placeholder: 'e.g. Jane Doe',
      icon: UserIcon
    }, {
      id: 'relationship',
      label: 'Relationship',
      type: 'select',
      options: ['Spouse', 'Child', 'Sibling', 'Parent']
    }, {
      id: 'health',
      label: 'Health Declaration',
      type: 'select',
      options: ['I am in good health', 'I have pre-existing conditions']
    }],
    plans: [{
      id: 'basic',
      name: 'Basic Cover',
      type: 'Term Life',
      premium: 3000,
      returns: 'No Savings',
      features: ['Death Benefit', 'Terminal Illness Cover'],
      popular: false
    }, {
      id: 'enhanced',
      name: 'Enhanced Cover',
      type: 'Endowment',
      premium: 5500,
      returns: '5% Projected',
      features: ['Death Benefit', 'Permanent Disability', 'Maturity Cash Value'],
      popular: true
    }, {
      id: 'maximum',
      name: 'Maximum Cover',
      type: 'Endowment+',
      premium: 10000,
      returns: '8% Projected',
      features: ['All Enhanced Benefits', 'Critical Illness Cover', 'Contribution Waiver'],
      popular: false
    }]
  },
  annuity: {
    id: 'annuity',
    name: 'Freedom Fund',
    pricingType: 'flexible',
    onboardingTitle: 'Retirement Profile',
    onboardingDesc: 'Tell us about your retirement timeline to set up your annuity.',
    icon: TrendingUpIcon,
    color: 'purple',
    underwriter: 'Stanbic IBTC Insurance',
    certificateTitle: 'Freedom Fund Plan',
    onboardingFields: [{
      id: 'employer',
      label: 'Current Employer',
      type: 'text',
      placeholder: 'e.g. Ministry of Education',
      icon: BuildingIcon
    }, {
      id: 'retirementAge',
      label: 'Expected Retirement Age',
      type: 'select',
      options: ['55', '60', '65', '70']
    }],
    plans: [{
      id: 'conservative',
      name: 'Conservative',
      type: 'Low Risk',
      premium: 10000,
      returns: '7% Guaranteed',
      features: ['Capital Preservation', 'Fixed Annuity Payouts', 'Annual Statements'],
      popular: false
    }, {
      id: 'balanced',
      name: 'Balanced',
      type: 'Medium Risk',
      premium: 25000,
      returns: '10% Projected',
      features: ['Inflation Protection', 'Variable Annuity Payouts', 'Quarterly Statements'],
      popular: true
    }, {
      id: 'aggressive',
      name: 'Aggressive',
      type: 'High Risk',
      premium: 50000,
      returns: '14% Projected',
      features: ['Maximum Growth Potential', 'Market-Linked Payouts', 'Monthly Statements'],
      popular: false
    }]
  },
  home: {
    id: 'home',
    name: 'Property Shield',
    onboardingTitle: 'Property Details',
    onboardingDesc: 'Provide the details of the property you want to protect.',
    icon: HomeIcon,
    color: 'amber',
    underwriter: 'Cornerstone Insurance',
    certificateTitle: 'Property Shield Plan',
    onboardingFields: [{
      id: 'address',
      label: 'Property Address',
      type: 'text',
      placeholder: 'e.g. 123 Awolowo Road, Ikoyi',
      icon: HomeIcon
    }, {
      id: 'occupancy',
      label: 'Occupancy Type',
      type: 'select',
      options: ['Owner Occupied', 'Rented (Tenant)', 'Landlord (Rented Out)']
    }],
    plans: [{
      id: 'building',
      name: 'Building Only',
      type: 'Basic',
      premium: 4000,
      returns: 'No Savings',
      features: ['Fire & Lightning', 'Flood & Storm', 'Malicious Damage'],
      popular: false
    }, {
      id: 'contents',
      name: 'Contents Only',
      type: 'Basic',
      premium: 3000,
      returns: 'No Savings',
      features: ['Theft & Burglary', 'Fire Damage to Contents', 'Alternative Accommodation'],
      popular: false
    }, {
      id: 'comprehensive',
      name: 'Comprehensive',
      type: 'Full Cover',
      premium: 6500,
      returns: 'Optional Savings',
      features: ['Building & Contents', 'Public Liability', 'Alternative Accommodation', 'Home Emergency Cover'],
      popular: true
    }]
  },
  health: {
    id: 'health',
    name: 'Wellness Plan',
    onboardingTitle: 'Medical Profile',
    onboardingDesc: 'Set up your health profile for your HMO coverage.',
    icon: StethoscopeIcon,
    color: 'cyan',
    underwriter: 'Hygeia HMO',
    certificateTitle: 'Wellness Plan',
    onboardingFields: [{
      id: 'preExisting',
      label: 'Pre-existing Conditions',
      type: 'select',
      options: ['None', 'Yes, I have pre-existing conditions']
    }, {
      id: 'pcp',
      label: 'Preferred Primary Care Hospital',
      type: 'select',
      options: ['Lagos University Teaching Hospital (Lagos)', 'National Hospital Abuja (FCT)', 'Reddington Hospital (Lagos)', 'Evercare Hospital (Lagos)', 'University College Hospital (Oyo)', 'Cedarcrest Hospital (FCT)', 'St. Nicholas Hospital (Lagos)', 'Nisa Premier Hospital (FCT)']
    }],
    plans: [{
      id: 'basic',
      name: 'Basic HMO',
      type: 'Bronze',
      premium: 8000,
      returns: 'No Savings',
      features: ['Outpatient Care (₦500k limit)', 'General Ward Admission', 'Basic Dental & Optical'],
      popular: false
    }, {
      id: 'standard',
      name: 'Standard HMO',
      type: 'Silver',
      premium: 15000,
      returns: 'No Savings',
      features: ['Outpatient Care (₦1.5M limit)', 'Semi-Private Room', 'Comprehensive Dental/Optical', 'Maternity Cover'],
      popular: true
    }, {
      id: 'premium',
      name: 'Premium Indemnity',
      type: 'Gold',
      premium: 35000,
      returns: 'Optional Savings',
      features: ['Unlimited Outpatient', 'Private Room Admission', 'International Evacuation', 'Full Maternity & Surgeries'],
      popular: false
    }]
  },
  travel: {
    id: 'travel',
    name: 'TravelGuard',
    onboardingTitle: 'Trip Details',
    onboardingDesc: 'Enter the details of your upcoming trip.',
    icon: PlaneIcon,
    color: 'slate',
    underwriter: 'AXA Mansard',
    certificateTitle: 'TravelGuard Plan',
    onboardingFields: [{
      id: 'destination',
      label: 'Destination Type',
      type: 'select',
      options: ['Local (Within Nigeria)', 'International']
    }, {
      id: 'duration',
      label: 'Trip Duration (Days)',
      type: 'text',
      placeholder: 'e.g. 14',
      icon: CalendarIcon
    }],
    plans: [{
      id: 'local',
      name: 'Local TravelGuard',
      type: 'Basic',
      premium: 5000,
      returns: 'No Savings',
      features: ['Trip Cancellation', 'Medical Emergencies', 'Lost Baggage'],
      popular: false
    }, {
      id: 'international',
      name: 'International TravelGuard',
      type: 'Comprehensive',
      premium: 25000,
      returns: 'No Savings',
      features: ['Worldwide Cover', 'Medical Evacuation', 'Flight Delay', 'Lost Passport'],
      popular: true
    }]
  }
};
export function PlanSelection({
  navigate,
  productId = 'goalguard',
  viewMode = 'app'
}: PlanSelectionProps) {
  const [step, setStep] = useState<Step>('plans');
  // Fallback to goalguard if invalid productId
  const config = productConfigs[productId] || productConfigs['goalguard'];
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'card' | 'bank'>('wallet');
  const walletBalance = 2450000;
  const sumAssured = 10000000;
  // Initialize form data with default selects
  useEffect(() => {
    const initialData: Record<string, string> = {};
    config.onboardingFields.forEach(field => {
      if (field.type === 'select' && field.options && field.options.length > 0) {
        initialData[field.id] = field.options[0];
      } else {
        initialData[field.id] = '';
      }
    });
    setFormData(initialData);
  }, [config.id]);
  // Generate confetti pieces for the success screen
  const confetti = Array.from({
    length: 40
  }).map((_, i) => ({
    id: i,
    x: Math.random() * 300 - 150,
    y: Math.random() * -300 - 50,
    rotation: Math.random() * 360,
    scale: Math.random() * 0.5 + 0.5,
    color: ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6'][Math.floor(Math.random() * 4)]
  }));
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => {
        setStep('success');
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [step]);
  const handleBack = () => {
    if (step === 'plans') navigate('product-detail', config.id);else if (step === 'onboarding') setStep('plans');else if (step === 'payment') setStep('onboarding');else if (step === 'review') setStep('payment');
  };
  const getTopBarTitle = () => {
    switch (step) {
      case 'plans':
        return 'Choose Your Plan';
      case 'onboarding':
        return config.onboardingTitle;
      case 'payment':
        return 'Payment Method';
      case 'review':
        return 'Review & Pay';
      default:
        return '';
    }
  };
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  const isOnboardingValid = config.onboardingFields.every(f => formData[f.id] && formData[f.id].trim() !== '');
  const handleInputChange = (id: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };
  return <div className="flex-1 overflow-hidden bg-slate-50 flex flex-col h-full relative" data-id="element-2362">
      {step !== 'processing' && step !== 'success' && <TopBar title={getTopBarTitle()} showBack onBack={handleBack} data-id="element-2363" />}

      <div className="flex-1 relative overflow-hidden" data-id="element-2364">
        <AnimatePresence mode="wait" data-id="element-2365">
          {step === 'plans' && <motion.div key="plans" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-12 px-5 pt-4" data-id="element-2366">
              <div className="mb-8" data-id="element-2367">
                <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-2368">
                  Choose Your Plan
                </h1>
                <p className="text-sm text-gray-500" data-id="element-2369">
                  Select a {config.name.toLowerCase()} plan that best fits your
                  needs.
                </p>
              </div>

              {config.pricingType === 'flexible' && <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-6 flex items-start gap-3" data-id="element-2370">
                  <InfoIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" data-id="element-2371" />
                  <p className="text-xs text-blue-800 leading-relaxed" data-id="element-2372">
                    {config.plans.some(p => p.returns === 'No Savings') ? 'Endowment tiers have flexible contributions based on your savings goal. The Term Life tier has a fixed monthly premium.' : 'Your actual contribution is flexible based on your savings goal. Each tier has a minimum monthly contribution and unlocks different features.'}
                  </p>
                </div>}

              <div className={viewMode === 'tablet' ? 'grid grid-cols-3 gap-6 pb-6' : 'flex overflow-x-auto snap-x snap-mandatory gap-4 pb-6 -mx-5 px-5 scrollbar-hide shrink-0'} data-id="element-2373">
                {config.plans.map((plan, idx) => {
              const isPlanFlexible = config.pricingType === 'flexible' && plan.returns !== 'No Savings';
              return <motion.div key={plan.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.1
              }} className={`relative bg-white rounded-3xl p-6 border-2 transition-all flex flex-col h-full ${viewMode === 'tablet' ? 'min-w-0 w-auto' : 'min-w-[85vw] snap-center'} ${plan.popular ? `border-amber-500 shadow-xl shadow-amber-500/10 ${viewMode === 'tablet' ? 'scale-[1.02]' : ''}` : 'border-gray-100 shadow-sm'}`} data-id="element-2374">
                      {plan.popular && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-amber-500 text-[#0D3B3B] px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-sm whitespace-nowrap" data-id="element-2375">
                          <StarIcon className="w-3.5 h-3.5 fill-[#0D3B3B]" data-id="element-2376" />{' '}
                          Most Popular
                        </div>}

                      <div className="flex justify-between items-start mb-6" data-id="element-2377">
                        <div data-id="element-2378">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2379">
                            {plan.type}
                          </p>
                          <h3 className="text-xl font-bold text-[#0D3B3B]" data-id="element-2380">
                            {plan.name}
                          </h3>
                        </div>
                        <div className="text-right" data-id="element-2381">
                          {isPlanFlexible && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-2382">
                              From
                            </p>}
                          <p className="text-2xl font-bold text-[#0D3B3B]" data-id="element-2383">
                            {formatNaira(plan.premium)}
                          </p>
                          <p className="text-xs text-gray-500 font-medium" data-id="element-2384">
                            {isPlanFlexible ? 'min. contribution' : '/month'}
                          </p>
                        </div>
                      </div>

                      {plan.returns !== 'No Savings' && <div className={`bg-${config.color}-50 rounded-xl p-3 mb-6 flex items-center gap-3 border border-${config.color}-100`} data-id="element-2385">
                          <div className={`w-8 h-8 rounded-full bg-${config.color}-100 flex items-center justify-center text-${config.color}-600 shrink-0`} data-id="element-2386">
                            <TrendingUpIcon className="w-4 h-4" data-id="element-2387" />
                          </div>
                          <div data-id="element-2388">
                            <p className={`text-xs font-bold text-${config.color}-800`} data-id="element-2389">
                              Expected Returns
                            </p>
                            <p className={`text-sm font-bold text-${config.color}-600`} data-id="element-2390">
                              {plan.returns}
                            </p>
                          </div>
                        </div>}

                      <div className="space-y-3 mb-8" data-id="element-2391">
                        {plan.features.map((feature, fIdx) => <div key={fIdx} className="flex items-start gap-3" data-id="element-2392">
                            <CheckIcon className={`w-5 h-5 text-${config.color}-500 shrink-0`} data-id="element-2393" />
                            <span className="text-sm text-gray-600 font-medium" data-id="element-2394">
                              {feature}
                            </span>
                          </div>)}
                      </div>

                      <button onClick={() => {
                  setSelectedPlan(plan);
                  setStep('onboarding');
                }} className={`w-full min-h-[52px] py-3.5 px-4 rounded-xl font-bold text-sm transition-all mt-auto shrink-0 flex items-center justify-center ${plan.popular ? 'bg-[#0D3B3B] text-white hover:bg-[#1a5c5c] shadow-md shadow-[#0D3B3B]/20' : 'bg-gray-50 text-[#0D3B3B] hover:bg-gray-100 border border-gray-200'}`} data-id="element-2395">
                        Select {plan.name}
                      </button>
                    </motion.div>;
            })}
              </div>
            </motion.div>}

          {step === 'onboarding' && <motion.div key="onboarding" variants={pageVariants} initial="initial" animate="animate" exit="exit" className={`absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col ${viewMode === 'tablet' ? 'items-center' : ''}`} data-id="element-2396">
              <div className="w-full max-w-lg" data-id="element-2397">
                <div className="mb-8" data-id="element-2398">
                  <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-2399">
                    {config.onboardingTitle}
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-2400">
                    {config.onboardingDesc}
                  </p>
                </div>

                <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-2401">
                  <div className="space-y-5" data-id="element-2402">
                    {config.onboardingFields.map(field => <div key={field.id} data-id="element-2403">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2404">
                          {field.label}
                        </label>

                        {field.type === 'select' ? <select value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3.5 px-4 text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] appearance-none" data-id="element-2405">
                            {field.options?.map(opt => <option key={opt} value={opt} data-id="element-2406">
                                {opt}
                              </option>)}
                          </select> : <div className="relative" data-id="element-2407">
                            {field.icon && <field.icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2408" />}
                            <input type={field.type} value={formData[field.id] || ''} onChange={e => handleInputChange(field.id, e.target.value)} placeholder={field.placeholder} className={`w-full bg-gray-50 border border-gray-200 rounded-xl py-3.5 pr-4 text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] ${field.icon ? 'pl-12' : 'pl-4'}`} data-id="element-2409" />
                          </div>}
                      </div>)}
                  </div>
                </div>

                {config.id === 'child-education' && <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100 flex items-start gap-3 mb-8" data-id="element-2410">
                    <ShieldCheckIcon className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" data-id="element-2411" />
                    <p className="text-xs text-emerald-800 leading-relaxed" data-id="element-2412">
                      <strong data-id="element-2413">Life Insurance Guarantee:</strong> If you pass
                      away before the plan matures, the full ₦10,000,000 target
                      is guaranteed to be paid for{' '}
                      {formData['childName'] || 'your child'}'s education.
                    </p>
                  </div>}

                <div className="mt-auto pb-6 pt-4" data-id="element-2414">
                  <button disabled={!isOnboardingValid} onClick={() => setStep('payment')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 ${isOnboardingValid ? 'bg-[#0D3B3B] hover:bg-[#092D2D] text-white shadow-lg shadow-emerald-900/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2415">
                    Continue
                  </button>
                </div>
              </div>
            </motion.div>}

          {step === 'payment' && <motion.div key="payment" variants={pageVariants} initial="initial" animate="animate" exit="exit" className={`absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col ${viewMode === 'tablet' ? 'items-center' : ''}`} data-id="element-2416">
              <div className="w-full max-w-lg" data-id="element-2417">
                <div className="mb-8" data-id="element-2418">
                  <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-2419">
                    How would you like to pay?
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-2420">
                    Select a payment method for your first contribution of{' '}
                    <strong className="text-[#0D3B3B]" data-id="element-2421">
                      {formatNaira(selectedPlan?.premium || 0)}
                    </strong>
                    {config.pricingType === 'flexible' && selectedPlan?.returns !== 'No Savings' ? ` (minimum for ${selectedPlan?.name})` : ''}
                  </p>
                </div>

                <div className="space-y-4 mb-8" data-id="element-2422">
                  {/* Skydd Wallet */}
                  <div onClick={() => setPaymentMethod('wallet')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${paymentMethod === 'wallet' ? 'border-[#0D3B3B] bg-emerald-50/50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-2423">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${paymentMethod === 'wallet' ? 'bg-[#0D3B3B] text-white' : 'bg-gray-50 text-gray-500'}`} data-id="element-2424">
                      <WalletIcon className="w-6 h-6" data-id="element-2425" />
                    </div>
                    <div className="flex-1" data-id="element-2426">
                      <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-2427">
                        Skydd Wallet
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-2428">
                        Balance: {formatNaira(walletBalance)}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'wallet' ? 'border-[#0D3B3B]' : 'border-gray-300'}`} data-id="element-2429">
                      {paymentMethod === 'wallet' && <div className="w-2.5 h-2.5 bg-[#0D3B3B] rounded-full" data-id="element-2430"></div>}
                    </div>
                  </div>

                  {/* Paystack Card */}
                  <div onClick={() => setPaymentMethod('card')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${paymentMethod === 'card' ? 'border-[#0D3B3B] bg-emerald-50/50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-2431">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${paymentMethod === 'card' ? 'bg-[#0D3B3B] text-white' : 'bg-gray-50 text-gray-500'}`} data-id="element-2432">
                      <CreditCardIcon className="w-6 h-6" data-id="element-2433" />
                    </div>
                    <div className="flex-1" data-id="element-2434">
                      <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-2435">
                        Debit Card
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-2436">
                        Powered by Paystack
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'card' ? 'border-[#0D3B3B]' : 'border-gray-300'}`} data-id="element-2437">
                      {paymentMethod === 'card' && <div className="w-2.5 h-2.5 bg-[#0D3B3B] rounded-full" data-id="element-2438"></div>}
                    </div>
                  </div>

                  {/* Bank Transfer */}
                  <div onClick={() => setPaymentMethod('bank')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${paymentMethod === 'bank' ? 'border-[#0D3B3B] bg-emerald-50/50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-2439">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${paymentMethod === 'bank' ? 'bg-[#0D3B3B] text-white' : 'bg-gray-50 text-gray-500'}`} data-id="element-2440">
                      <BuildingIcon className="w-6 h-6" data-id="element-2441" />
                    </div>
                    <div className="flex-1" data-id="element-2442">
                      <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-2443">
                        Bank Transfer
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-2444">
                        Direct bank transfer
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'bank' ? 'border-[#0D3B3B]' : 'border-gray-300'}`} data-id="element-2445">
                      {paymentMethod === 'bank' && <div className="w-2.5 h-2.5 bg-[#0D3B3B] rounded-full" data-id="element-2446"></div>}
                    </div>
                  </div>
                </div>

                <div className="mt-auto pb-6 pt-4" data-id="element-2447">
                  <button onClick={() => setStep('review')} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] hover:bg-[#092D2D] text-white shadow-lg shadow-emerald-900/20 transition-all active:scale-95 flex items-center justify-center gap-2" data-id="element-2448">
                    Continue <ArrowRightIcon className="w-5 h-5" data-id="element-2449" />
                  </button>
                </div>
              </div>
            </motion.div>}

          {step === 'review' && <motion.div key="review" variants={pageVariants} initial="initial" animate="animate" exit="exit" className={`absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col ${viewMode === 'tablet' ? 'items-center' : ''}`} data-id="element-2450">
              <div className="w-full max-w-lg" data-id="element-2451">
                <div className="mb-8" data-id="element-2452">
                  <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-2453">
                    Review & Confirm
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-2454">
                    Please review your plan details before making payment.
                  </p>
                </div>

                <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-2455">
                  <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100" data-id="element-2456">
                    <div className={`w-14 h-14 rounded-2xl bg-${config.color}-100 flex items-center justify-center text-${config.color}-600 shrink-0`} data-id="element-2457">
                      <config.icon className="w-7 h-7" data-id="element-2458" />
                    </div>
                    <div data-id="element-2459">
                      <h3 className="font-bold text-[#0D3B3B] text-lg" data-id="element-2460">
                        {selectedPlan?.name} ({selectedPlan?.type})
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-2461">{config.name}</p>
                    </div>
                  </div>

                  <div className="space-y-5" data-id="element-2462">
                    {config.onboardingFields.map(field => <div key={field.id} className="flex justify-between items-center" data-id="element-2463">
                        <span className="text-sm text-gray-500" data-id="element-2464">
                          {field.label}
                        </span>
                        <span className="text-sm font-bold text-[#0D3B3B] max-w-[60%] text-right truncate" data-id="element-2465">
                          {formData[field.id]}
                        </span>
                      </div>)}

                    <div className="flex justify-between items-center pt-2 border-t border-gray-50 border-dashed" data-id="element-2466">
                      <span className="text-sm text-gray-500" data-id="element-2467">
                        Payment Method
                      </span>
                      <span className="text-sm font-bold text-[#0D3B3B] capitalize" data-id="element-2468">
                        {paymentMethod === 'wallet' ? 'Skydd Wallet' : paymentMethod === 'card' ? 'Debit Card' : 'Bank Transfer'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-5 border-t border-gray-100" data-id="element-2469">
                      <span className="text-base font-bold text-[#0D3B3B]" data-id="element-2470">
                        First Contribution{' '}
                        {config.pricingType === 'flexible' && selectedPlan?.returns !== 'No Savings' ? '(Minimum)' : ''}
                      </span>
                      <span className="text-xl font-bold text-[#0D3B3B]" data-id="element-2471">
                        {formatNaira(selectedPlan?.premium || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 text-center mb-6 leading-relaxed px-4" data-id="element-2472">
                  By tapping "Confirm & Pay", you agree to the terms and
                  conditions and authorize this payment.
                </p>

                <div className="mt-auto pb-6 pt-4" data-id="element-2473">
                  <button onClick={() => setStep('processing')} className="w-full py-4 rounded-xl font-bold text-lg bg-[#E85D3A] hover:bg-[#D04E2E] text-white shadow-lg shadow-amber-500/30 transition-all active:scale-95" data-id="element-2474">
                    Confirm & Pay {formatNaira(selectedPlan?.premium || 0)}{' '}
                    {config.pricingType === 'flexible' && selectedPlan?.returns !== 'No Savings' ? '(min.)' : ''}
                  </button>
                </div>
              </div>
            </motion.div>}

          {step === 'processing' && <motion.div key="processing" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50" data-id="element-2475">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 1,
            ease: 'linear'
          }} className="mb-6" data-id="element-2476">
                <Loader2Icon className="w-12 h-12 text-amber-500" data-id="element-2477" />
              </motion.div>
              <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-2478">
                Activating Plan
              </h2>
              <p className="text-sm text-gray-500" data-id="element-2479">
                Securely processing your payment and generating certificate...
              </p>
            </motion.div>}

          {step === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-8 flex flex-col bg-gradient-to-b from-[#06332E] via-[#06332E] to-[#0a4a43]" data-id="element-2480">
              {/* Confetti */}
              {confetti.map(c => <motion.div key={c.id} initial={{
            opacity: 1,
            x: 0,
            y: 0,
            scale: 0
          }} animate={{
            opacity: 0,
            x: c.x,
            y: c.y,
            scale: c.scale,
            rotate: c.rotation
          }} transition={{
            duration: 2.5,
            ease: 'easeOut'
          }} className="absolute left-1/2 top-1/3 w-3 h-3 rounded-sm pointer-events-none z-50" style={{
            backgroundColor: c.color
          }} data-id="element-2481" />)}

              <div className="text-center mb-6" data-id="element-2482">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              damping: 15
            }} className="mx-auto mb-4" data-id="element-2483">
                  <img src="https://cdn.magicpatterns.com/uploads/7UEvgvkXpEAQMdiRCwr1Yk/Layer_21.png" alt="Success mascot" className="w-48 h-48 object-contain mx-auto drop-shadow-2xl" data-id="element-2484" />
                </motion.div>

                <h1 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-2485">
                  Plan Activated!
                </h1>
                <p className="text-sm text-white/60 max-w-[260px] mx-auto" data-id="element-2486">
                  Your {config.name} plan is now active.
                </p>
              </div>

              {/* Temporary Certificate Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/10 mb-8 text-white relative overflow-hidden" data-id="element-2487">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-2488"></div>

                <div className="flex justify-between items-start mb-6 relative z-10" data-id="element-2489">
                  <div data-id="element-2490">
                    <div className={`text-[#FFD700] text-[10px] font-bold uppercase tracking-widest mb-1`} data-id="element-2491">
                      Temporary Certificate
                    </div>
                    <div className="font-bold text-lg" data-id="element-2492">
                      {config.certificateTitle}
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-2493">
                    <FileTextIcon className="w-5 h-5 text-white" data-id="element-2494" />
                  </div>
                </div>

                <div className="space-y-4 relative z-10" data-id="element-2495">
                  <div data-id="element-2496">
                    <div className="text-white/50 text-xs mb-1" data-id="element-2497">
                      Plan Number
                    </div>
                    <div className="font-mono font-bold tracking-wider" data-id="element-2498">
                      SKD-{config.id.substring(0, 3).toUpperCase()}-
                      {Math.floor(Math.random() * 90000) + 10000}
                    </div>
                  </div>
                  <div className="flex justify-between" data-id="element-2499">
                    <div data-id="element-2500">
                      <div className="text-white/50 text-xs mb-1" data-id="element-2501">Plan</div>
                      <div className="font-bold" data-id="element-2502">{selectedPlan?.name}</div>
                    </div>
                    <div className="text-right" data-id="element-2503">
                      <div className="text-white/50 text-xs mb-1" data-id="element-2504">
                        Underwriter
                      </div>
                      <div className="font-bold text-sm" data-id="element-2505">
                        {config.underwriter}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-auto pb-6 space-y-3" data-id="element-2506">
                <button onClick={() => navigate('policies')} className="w-full py-4 rounded-xl font-bold text-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white shadow-sm flex items-center justify-center gap-2 transition-all active:scale-95 hover:bg-white/15" data-id="element-2507">
                  <FileTextIcon className="w-5 h-5" data-id="element-2508" /> View Plan Details
                </button>
                <button onClick={() => navigate('dashboard')} className="w-full py-4 rounded-xl font-bold text-lg bg-[#FFD700] text-[#06332E] shadow-lg shadow-black/20 transition-all active:scale-95 hover:bg-[#FFE033]" data-id="element-2509">
                  Back to Home
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}